import type { DateTime } from 'luxon';
import { type ChangeEvent, createContext, type FC, type MouseEvent, type ReactElement, useState } from 'react';
import { usePagination } from '../../content/hooks';
import type { ClientModel, Order, VenueModel, VisitTapReportData, VisitWastageSortingFields } from '../../models';

interface IReportsContext {
  loading: boolean;
  setLoading: (val: boolean) => void;
  dataUpdate: boolean;
  toggleUpdate: () => void;
  data: VisitTapReportData[];
  setData: (val: VisitTapReportData[]) => void;
  page: number;
  setPage: (val: number) => void;
  limit: number;
  setLimit: (val: number) => void;
  order: Order;
  setOrder: (val: Order) => void;
  orderBy: VisitWastageSortingFields;
  setOrderBy: (val: VisitWastageSortingFields) => void;
  handlePageChange: (_event: any, newPage: number) => void;
  handleLimitChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleRequestSort: (_event: MouseEvent, property: VisitWastageSortingFields) => void;
  count: number;
  setCount: (val: number) => void;
  fromDate: DateTime | null;
  setFromDate: (val: DateTime | null) => void;
  toDate: DateTime | null;
  setToDate: (val: DateTime | null) => void;
  client: ClientModel | null;
  setClient: (val: ClientModel | null) => void;
  venues: VenueModel[];
  setVenues: (val: VenueModel[]) => void;
}

export const VisitTapsContext = createContext<IReportsContext>({} as IReportsContext);

export const VisitTapsProvider: FC<{ children: ReactElement }> = ({ children }) => {
  const [dataUpdate, setDataUpdate] = useState(false);
  const toggleUpdate = (): void => setDataUpdate(!dataUpdate);

  const {
    page,
    setPage,
    limit,
    setLimit,
    count,
    setCount,
    order,
    setOrder,
    orderBy,
    setOrderBy,
    handlePageChange,
    handleLimitChange,
    handleRequestSort
  } = usePagination<VisitWastageSortingFields>(toggleUpdate, 'dateOfVisit', 'desc');

  const [data, setData] = useState<VisitTapReportData[]>([]);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState<DateTime | null>(null);
  const [toDate, setToDate] = useState<DateTime | null>(null);
  const [client, setClient] = useState<ClientModel | null>(null);
  const [venues, setVenues] = useState<VenueModel[]>([]);

  return (
    <VisitTapsContext.Provider
      value={{
        data,
        setData,
        page,
        setPage,
        limit,
        setLimit,
        count,
        setCount,
        order,
        setOrder,
        orderBy,
        setOrderBy,
        handlePageChange,
        handleLimitChange,
        handleRequestSort,
        loading,
        setLoading,
        dataUpdate,
        toggleUpdate,
        fromDate,
        setFromDate,
        toDate,
        setToDate,
        client,
        setClient,
        venues,
        setVenues
      }}
    >
      {children}
    </VisitTapsContext.Provider>
  );
};
